import React from 'react';
import { Wind, Waves, Sun, CloudRain, RefreshCw } from 'lucide-react';
import { Button } from '../common';
import { getWeatherDescription } from '../../constants/weatherCodes';
import { getNextTide } from '../../utils/weatherHelpers';
import { getWeatherIcon } from '../../constants/weatherCodes';
import { isFlagDataStale } from '../../utils/dateHelpers';
import { CONFIG } from '../../constants/config';

const WeatherDisplay = ({ 
  weather, 
  tides, 
  waterTemp, 
  moonData, 
  flagStatus, 
  onRefresh, 
  onUpdateFlag 
}) => {
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-4xl font-bold text-gray-800 dark:text-gray-100">
            {weather?.current?.temperature_2m ? `${Math.round(weather.current.temperature_2m)}°F` : '--°F'}
          </h3>
          <p className="text-lg text-gray-600 dark:text-gray-400 flex items-center gap-2 mt-1">
            {weather?.current?.weather_code !== undefined && (
              <>
                <span className="text-3xl drop-shadow-md">{getWeatherIcon(weather.current.weather_code)}</span>
                {getWeatherDescription(weather.current.weather_code)}
              </>
            )}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Feels like {weather?.current?.apparent_temperature ? `${Math.round(weather.current.apparent_temperature)}°F` : '--°F'}
          </p>
        </div>
        <div className="text-right">
          <div className={`text-4xl mb-2 ${flagStatus.color !== '🟢' ? 'animate-pulse-flag' : ''} relative`}>
            {flagStatus.color}
            {flagStatus.lastChecked && isFlagDataStale(flagStatus.lastChecked, CONFIG.BEACH_HOURS) && (
              <span className="absolute -top-1 -right-1">
                <span className="absolute inline-flex h-3 w-3 rounded-full bg-orange-400 opacity-75 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
              </span>
            )}
          </div>
          <p className="text-sm font-bold text-gray-700 dark:text-gray-300">{flagStatus.text.split(' - ')[0]}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{flagStatus.text.split(' - ')[1]}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg p-2 text-center">
          <Wind className="w-5 h-5 text-blue-600 mx-auto mb-1" />
          <p className="text-xs text-gray-800 dark:text-gray-200">Wind</p>
          <p className="text-sm font-bold">
            {weather?.current?.wind_speed_10m ? `${Math.round(weather.current.wind_speed_10m)} mph` : '--'}
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg p-2 text-center">
          <Waves className="w-5 h-5 text-blue-600 mx-auto mb-1" />
          <p className="text-xs text-gray-800 dark:text-gray-200">Water</p>
          <p className="text-sm font-bold">
            {waterTemp ? `${Math.round(waterTemp)}°F` : '--°F'}
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/30 rounded-lg p-2 text-center">
          <Sun className="w-5 h-5 text-orange-600 mx-auto mb-1" />
          <p className="text-xs text-gray-800 dark:text-gray-200">UV</p>
          <p className="text-sm font-bold">
            {weather?.daily?.uv_index_max?.[0] ? Math.round(weather.daily.uv_index_max[0]) : '--'}
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg p-2 text-center">
          <CloudRain className="w-5 h-5 text-gray-600 mx-auto mb-1" />
          <p className="text-xs text-gray-800 dark:text-gray-200">Rain</p>
          <p className="text-sm font-bold">
            --
          </p>
        </div>
      </div>
      
      {/* Today's Key Info */}
      <div className="border-t border-gray-200 dark:border-gray-600 pt-3 space-y-2">
        <div className="flex justify-between text-sm">
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">Sunrise</p>
            <p className="font-semibold">
              🌅 {weather?.daily?.sunrise?.[0] ? new Date(weather.daily.sunrise[0]).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '--'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">Next Tide</p>
            <p className="font-semibold">
              🌊 {(() => {
                const nextTide = getNextTide(tides);
                return nextTide ? `${nextTide.type} ${nextTide.time}` : '--';
              })()}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">Sunset</p>
            <p className="font-semibold">
              🌇 {weather?.daily?.sunset?.[0] ? new Date(weather.daily.sunset[0]).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '--'}
            </p>
          </div>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {moonData?.current?.emoji} {moonData?.current?.name}
          </p>
        </div>
      </div>
      
      {/* Tide Schedule - Show next 4 tides */}
      {tides && tides.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-600 pt-3 mt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">Today's Tides</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {tides.slice(0, 4).map((tide, idx) => {
              const tideTime = new Date(tide.t);
              const isHigh = tide.type === 'H';
              const isPast = tideTime < new Date();
              return (
                <div key={idx} className={`flex items-center gap-1 ${isPast ? 'opacity-50' : ''}`}>
                  <span>{isHigh ? '⬆️' : '⬇️'}</span>
                  <span className="font-medium">{isHigh ? 'High' : 'Low'}</span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {tideTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      <div className="mt-3 flex justify-between items-center">
        <a 
          href="https://www.facebook.com/sgcfire/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs text-orange-600 dark:text-orange-400 hover:underline"
        >
          Check beach flag sources
        </a>
        <div className="flex items-center gap-2">
          <Button
            onClick={onRefresh}
            variant="ghost"
            size="small"
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1"
            title="Refresh weather"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            onClick={onUpdateFlag}
            variant="link"
            size="small"
            className="text-xs flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" />
            Update Flag
          </Button>
        </div>
      </div>
    </>
  );
};

export default WeatherDisplay;